import { reactive, toRefs, watch } from 'vue'
import type { ToRefs, ComputedRef } from 'vue'

import type { DrillLayer, DrillLabelLayer } from '~/entities/drillLabel'
import type { DrillLegendViewModel } from '~/entities/drillHole'
import type { LayerGroup, UseThreeDViewer } from '~/composables/useThreeDViewer'
import { isDrillDataSetLayerGroup } from '~/composables/useThreeDViewer'

type State = {
  selectedLegends: DrillLegendViewModel[]
  selectedLegendByDataSetId: Record<number, Set<string>>
}

export type UseAssayLegends = {
  layerListGroupsWithLegends: ComputedRef<LayerGroup[]>
  addLegend: (id: string) => void
  removeLegend: (id: string) => void
  toggleLegendVisibility: (param: { id: string; visible: boolean }) => void
  getDefaultLegend: (dataSetId?: number) => DrillLegendViewModel | undefined
  getLegendsForDataSet: (dataSetId: number) => DrillLegendViewModel[]
  setActiveLegend: (legendId: string) => void
  removeSelectedLegendsForDataSet: (dataSetId: number) => void
} & ToRefs<State>

export function useAssayLegends({ threeDViewer }: { threeDViewer: UseThreeDViewer }): UseAssayLegends {
  const state = reactive<State>({
    selectedLegends: [],
    selectedLegendByDataSetId: {},
  })

  const layerListGroupsWithLegends = computed(() => {
    return (threeDViewer.layerGroups.value as LayerGroup[]).map(group => {
      return {
        ...group,
        layers: (group.layers as (DrillLayer | DrillLabelLayer)[]).filter(layer => {
          if (!isDrillDataSetLayerGroup(group)) return true

          const isNonLegendType = 'type' in layer && layer.type !== 'legend'
          const isSelectedLegend =
            'dataSetId' in group && state.selectedLegendByDataSetId[group.dataSetId as number]?.has(layer.id as string)

          return isNonLegendType || isSelectedLegend
        }),
      }
    }) as LayerGroup[]
  })

  watch(
    () => threeDViewer.layerGroups.value,
    layerGroups => {
      state.selectedLegends = []

      layerGroups.forEach((g: LayerGroup) => {
        if (!isDrillDataSetLayerGroup(g)) return

        g.layers
          .filter(l => l.type === 'legend')
          .forEach(l => {
            const registeredLegend = threeDViewer.world.drillDataSetManager?.drillLegendManager?.getLegendById(l.id as string)

            if (registeredLegend && registeredLegend.isSelected) {
              const drillIdsSet = state.selectedLegendByDataSetId[g.dataSetId as number]

              drillIdsSet?.forEach(legendId => {
                const selectedLegend = threeDViewer.world.drillDataSetManager?.drillLegendManager?.getLegendById(legendId)

                if (selectedLegend?.id !== registeredLegend.id) {
                  drillIdsSet.delete(legendId)
                }
              })

              const set = new Set(drillIdsSet)
              set.add(registeredLegend.id)

              state.selectedLegendByDataSetId[g.dataSetId as number] = set

              if (registeredLegend.visible) {
                state.selectedLegends = [...state.selectedLegends, registeredLegend]
              } else {
                state.selectedLegends = state.selectedLegends.filter(x => x.id !== registeredLegend.id)
              }
            }
          })
      })
    },
    { deep: true },
  )

  function addLegend(id: string): void {
    const legend = threeDViewer.world.drillDataSetManager?.drillLegendManager?.getLegendById(id)
    const isLegendAlreadySelected = state.selectedLegends.some(selectedLegend => selectedLegend.id === id)

    if (legend && legend.visible && !isLegendAlreadySelected) {
      state.selectedLegends = [...state.selectedLegends, legend]
      threeDViewer.setDrillHoleVisibility([{ drillLabelId: id, visible: true }])
    }
  }

  function removeLegend(id: string): void {
    const index = state.selectedLegends.findIndex(legend => legend.id === id)

    if (index !== -1) {
      state.selectedLegends = state.selectedLegends.filter(x => x.id !== id)
      threeDViewer.setDrillHoleVisibility([{ drillLabelId: id, visible: false }])
    }
  }

  function toggleLegendVisibility({ id, visible }: { id: string; visible: boolean }) {
    if (visible) {
      addLegend(id)
    } else {
      removeLegend(id)
    }
  }

  function getDefaultLegend(dataSetId?: number): DrillLegendViewModel | undefined {
    if (!threeDViewer.world.drillDataSetManager?.drillLegendManager) return

    const legendLayers = threeDViewer.layerGroups.value
      .filter(isDrillDataSetLayerGroup)
      .flatMap(x => x.layers)
      .filter(x => x.type === 'legend')

    const legends = threeDViewer.world.drillDataSetManager.drillLegendManager
      .getLegendList()
      .filter(l => dataSetId == null || l.dataSetId === dataSetId)

    const groupedLegends = legends.reduce((groups: Record<number, DrillLegendViewModel[]>, legend: DrillLegendViewModel) => {
      if (!groups[legend.dataSetId]) {
        groups[legend.dataSetId] = []
      }

      groups[legend.dataSetId].push(legend)

      return groups
    }, {})

    return _findFirstSelectedLegend(legendLayers, groupedLegends)
  }

  function _findFirstSelectedLegend(
    legendLayers: DrillLayer[],
    groupedLegends: Record<number, DrillLegendViewModel[]>,
  ): DrillLegendViewModel | undefined {
    for (const legendLayer of legendLayers) {
      const dataSetId = (legendLayer.id as string).split('-')[0]
      const legendInGroup = groupedLegends[parseInt(dataSetId)]

      if (!legendInGroup) continue

      const selectedLegend = legendInGroup.find((assay: DrillLegendViewModel) => assay.isSelected && assay.id === legendLayer.id)

      if (!selectedLegend) continue

      return selectedLegend
    }
  }

  function setActiveLegend(legendId: string) {
    threeDViewer.world.drillDataSetManager?.setActiveLegend(legendId)
    addLegend(legendId)
  }

  function removeSelectedLegendsForDataSet(dataSetId: number) {
    const legendId = state.selectedLegendByDataSetId[dataSetId]

    if (legendId) {
      delete state.selectedLegendByDataSetId[dataSetId]
    }

    state.selectedLegends = state.selectedLegends.filter(legend => legend.dataSetId !== dataSetId)
  }

  function getLegendsForDataSet(dataSetId: number): DrillLegendViewModel[] {
    return threeDViewer.world
      .drillDataSetManager!.drillLegendManager.getLegendList()
      .filter((l: DrillLegendViewModel) => l.dataSetId === dataSetId)
  }

  return {
    ...toRefs(state),
    layerListGroupsWithLegends,
    addLegend,
    removeLegend,
    toggleLegendVisibility,
    getDefaultLegend,
    getLegendsForDataSet,
    setActiveLegend,
    removeSelectedLegendsForDataSet,
  }
}
